import {
    ANAO,
    APESSOA_FISICA,
    APESSOA_JURIDICA,
    ASIM
} from 'consts';
import {
    getApiErrorMessage,
    getFormMode,
    getRecordFromResponse,
    getResponseData,
    MULTIPART_CONFIG,
    validateSchemaFields
} from 'lib/formUtils';
import {
    appendFieldsToFormData,
    getCodUsuarioRegistrou,
    getDateInputValue,
    isMissingValue
} from 'lib/functions';
import {
    ComboPadrao,
    FormActions,
    FormStatusSwitch,
    FormTextArea,
    ImagePreviewModal
} from 'views/Components';
import {
    alpha,
    Box,
    Button,
    CardContent,
    Checkbox,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography
} from '@material-ui/core';
import theme from 'theme';
import api from 'services/api';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { safeSetState } from 'lib/reactUtils';
import { CloudUploadOutlined } from '@material-ui/icons';
import { AlertInformation, DeleteConfirmationModal } from 'components';

/* Campos que seguem uppercase do Delphi */
const UPPERCASE_FIELDS = [
    'nomecontato',
    'fonecontato1',
    'fonecontato2',
    'fonecelular1',
    'fonecelular2',
    'numero',
    'bairro',
    'cep',
    'sitecontato',
    'twittercontato',
    'facebookcontato',
    'logradouro',
    'complemento',
    'cargo',
    'empresa'
];

/* Campos que seguem lowercase do Delphi */
const LOWERCASE_FIELDS = ['emailcontato'];

class ContatoForm extends Component {
    state = {
        contato: {
            codcontato: '',
            tipopessoa: APESSOA_FISICA,
            nomecontato: '',
            datanasc: '',
            fonecontato1: '',
            fonecontato2: '',
            fonecelular1: '',
            fonecelular2: '',
            emailcontato: '',
            sitecontato: '',
            twittercontato: '',
            facebookcontato: '',
            logradouro: '',
            numero: '',
            bairro: '',
            complemento: '',
            cep: '',
            codcidade: '',
            obsgeral: '',
            cargo: '',
            empresa: '',
            inativo: ANAO
        },
        cidades: [],
        gruposContato: [],
        gruposSelecionados: new Set(),
        fotoPreview: '',
        fotoNome: '',
        fotoFile: null,
        fotoRemove: false,
        fotoDragOver: false,
        fotoModalOpen: false,
        fotoRemoveModalOpen: false,
        cartaoPreview: '',
        cartaoNome: '',
        cartaoFile: null,
        cartaoRemove: false,
        cartaoDragOver: false,
        cartaoModalOpen: false,
        cartaoRemoveModalOpen: false,
        isLoading: true,
        isNewRecord: false,
        isView: false,
        openAlert: false,
        messageAlert: '',
        errors: {},
        schema: {
            nomecontato: {
                presence: {
                    allowEmpty: false,
                    message: 'Nome do contato obrigatório'
                },
                length: { maximum: 100 },
                type: 'string'
            }
        }
    };

    _isMounted = false;
    safeSetState = safeSetState;

    /* Carrega os dados iniciais da tela */
    async componentDidMount() {
        this._isMounted = true;

        const {
            id,
            isNewRecord,
            isView
        } = getFormMode(this.props.match, this.props.location);

        try {
            if (isNewRecord) {
                const [cidades, gruposContato] = await Promise.all([
                    this.loadCidades(),
                    this.loadGruposContato()
                ]);

                this.safeSetState({
                    isNewRecord: true,
                    isView: false,
                    isLoading: false,
                    contato: this.getContatoDefault(),
                    cidades,
                    gruposContato
                });

                return;
            }

            if (!isNaN(Number(id))) {
                await this.loadContato(id, isView);
            }
        } catch (error) {
            console.error(
                'Erro ao carregar o contato:',
                error.response?.data || error.message
            );

            this.handleOpenAlert(
                getApiErrorMessage(
                    error,
                    'Erro ao carregar o contato!'
                )
            );

            this.safeSetState({ isLoading: false });
        }
    };

    /* Limpa a flag de montagem ao sair da tela */
    componentWillUnmount() {
        this._isMounted = false;
    };

    /* Retorna o contato padrão */
    getContatoDefault = () => ({
        codcontato: '',
        tipopessoa: APESSOA_FISICA,
        nomecontato: '',
        datanasc: '',
        fonecontato1: '',
        fonecontato2: '',
        fonecelular1: '',
        fonecelular2: '',
        emailcontato: '',
        sitecontato: '',
        twittercontato: '',
        facebookcontato: '',
        logradouro: '',
        numero: '',
        bairro: '',
        complemento: '',
        cep: '',
        codcidade: '',
        obsgeral: '',
        cargo: '',
        empresa: '',
        inativo: ANAO
    });

    /* Carrega as cidades */
    loadCidades = async (codcidade) => {
        const url = codcidade ? `/cidade/listforchange/${codcidade}` : '/cidade/active/true?limit=10000';
        const response = await api.get(url);
        const cidades = getResponseData(response);

        // monta a descrição já com a UF pro combo
        return cidades.map(cidade => ({
            ...cidade,
            desccidadeuf: cidade.sigla ? `${cidade.desccidade} (${cidade.sigla})` : cidade.desccidade
        }));
    };

    /* Carrega os grupos de contato */
    loadGruposContato = async () => {
        const response = await api.get('/grupocontato/active/true');

        return getResponseData(response);
    };

    /* Carrega os grupos vinculados ao contato */
    loadGruposDoContato = async (codcontato) => {
        const response = await api.get(`/contato/${codcontato}/grupos`);
        const grupos = getResponseData(response);

        return new Set(grupos.map(grupo => Number(grupo.codgrupocontato)));
    };

    /* Carrega os contatos */
    loadContato = async (id, isView) => {
        const { history } = this.props;
        const response = await api.get(`/contato/${id}`);
        const registro = getRecordFromResponse(response);

        if (!registro) {
            this.handleOpenAlert('Contato não encontrado!');

            history.push('/crm/contato');

            return;
        }

        const [cidades, gruposContato, gruposSelecionados] = await Promise.all([
            this.loadCidades(registro.codcidade),
            this.loadGruposContato(),
            this.loadGruposDoContato(id)
        ]);

        this.safeSetState({
            contato: {
                ...registro,
                tipopessoa: registro.tipopessoa || APESSOA_FISICA,
                datanasc: registro.datanasc || '',
                inativo: registro.inativo || ANAO
            },
            cidades,
            gruposContato,
            gruposSelecionados,
            isNewRecord: false,
            isView,
            isLoading: false,
            fotoRemove: false,
            cartaoRemove: false,
            fotoPreview: registro.fotocontato || '',
            fotoNome: '',
            cartaoPreview: registro.imagemcartao || '',
            cartaoNome: ''
        });
    };

    /* Evento onChange componentes */
    onChange = (event) => {
        const { name, type, value, checked } = event.target;

        let adjustedValue;

        if (type === 'checkbox') {
            adjustedValue = checked ? ANAO : ASIM;
        } else if (UPPERCASE_FIELDS.includes(name)) {
            adjustedValue = String(value).toUpperCase();
        } else if (LOWERCASE_FIELDS.includes(name)) {
            adjustedValue = String(value).toLowerCase();
        } else {
            adjustedValue = value;
        }

        this.setState(prevState => ({
            contato: {
                ...prevState.contato,
                [name]: adjustedValue
            }
        }));
    };

    /* Marca ou desmarca um grupo de contato */
    onChangeGrupoContato = (codgrupocontato) => {
        this.setState(prevState => {
            const gruposSelecionados = new Set(prevState.gruposSelecionados);

            if (gruposSelecionados.has(codgrupocontato)) {
                gruposSelecionados.delete(codgrupocontato);
            } else {
                gruposSelecionados.add(codgrupocontato);
            }

            return { gruposSelecionados };
        });
    };

    /* Abre a janela de alerta com mensagem */
    handleOpenAlert = (message) => {
        this.safeSetState({
            openAlert: true,
            messageAlert: message
        });
    };

    /* Fecha a janela de mensagem alerta */
    handleCloseAlert = () => {
        this.setState({ openAlert: false });
    };

    /* Processa o arquivo de imagem selecionado ou arrastado */
    handleSelectedImage = (prefix, file, callback) => {
        if (!file) return;

        const reader = new FileReader();

        reader.onloadend = () => {
            this.setState({
                [`${prefix}Preview`]: reader.result,
                [`${prefix}File`]: file,
                [`${prefix}Nome`]: file.name,
                [`${prefix}Remove`]: false,
                [`${prefix}DragOver`]: false
            }, () => {
                if (callback) callback();
            });
        };

        reader.readAsDataURL(file);
    };

    /* Upload via input */
    handleImageChange = (prefix) => (event) => {
        const input = event.target;
        const file = input.files[0];

        this.handleSelectedImage(prefix, file, () => {
            input.value = '';
        });
    };

    /* Drag and drop */
    handleDragEnter = (prefix) => (event) => {
        const { isView } = this.state;

        event.preventDefault();
        event.stopPropagation();

        if (!isView) {
            this.setState({ [`${prefix}DragOver`]: true });
        }
    };

    /* Arrastar o arquivo */
    handleDragOver = (prefix) => (event) => {
        const { isView } = this.state;

        event.preventDefault();
        event.stopPropagation();

        if (!isView && ![`${prefix}DragOver`]) {
            this.setState({ [`${prefix}DragOver`]: true });
        }
    };

    /* Arrastar e sair */
    handleDragLeave = (prefix) => (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!event.currentTarget.contains(event.relatedTarget)) {
            this.setState({ [`${prefix}DragOver`]: false });
        }
    };

    /* Soltar o arquivo */
    handleDrop = (prefix) => (event) => {
        const { isView } = this.state;

        event.preventDefault();
        event.stopPropagation();

        if (isView) {
            this.setState({ [`${prefix}DragOver`]: false });

            return;
        }

        const file = event.dataTransfer?.files?.[0];

        this.handleSelectedImage(prefix, file);
    };
    /* Apaga a imagem */
    handleRemoveImage = (prefix) => () => {
        this.setState({
            [`${prefix}Preview`]: '',
            [`${prefix}File`]: null,
            [`${prefix}Nome`]: '',
            [`${prefix}Remove`]: true
        });
    };

    /* Abre o modal */
    handleOpenImageModal = (prefix) => (event) => {
        event.currentTarget.blur();

        this.setState({ [`${prefix}ModalOpen`]: true });
    };

    /* Fecha o modal */
    handleCloseImageModal = (prefix) => () => {
        this.setState({ [`${prefix}ModalOpen`]: false });
    };

    /* Abre a confirmação para a remoção da imagem */
    handleOpenRemoveImageModal = (prefix) => () => {
        this.setState({ [`${prefix}RemoveModalOpen`]: true });
    };

    /* Fecha a confirmação para a remoção da imagem */
    handleCloseRemoveImageModal = (prefix) => () => {
        this.setState({ [`${prefix}RemoveModalOpen`]: false });
    };

    /* Confirma a remoção da imagem */
    confirmRemoveImage = (prefix) => () => {
        this.setState({ [`${prefix}RemoveModalOpen`]: false }, () => {
            this.handleRemoveImage(prefix)();
        });
    };

    /* Retorna o payload do contato */
    getPayloadContato = () => {
        const {
            cartaoFile,
            cartaoRemove,
            contato,
            fotoFile,
            fotoRemove,
            gruposSelecionados
        } = this.state;

        const codusuarioregistrou = getCodUsuarioRegistrou();

        if (isMissingValue(codusuarioregistrou)) {
            throw new Error('Código do usuário não encontrado!');
        }

        const fields = {
            tipopessoa: contato.tipopessoa || APESSOA_FISICA,
            nomecontato: contato.nomecontato || '',
            datanasc: contato.datanasc || '',
            fonecontato1: contato.fonecontato1 || '',
            fonecontato2: contato.fonecontato2 || '',
            fonecelular1: contato.fonecelular1 || '',
            fonecelular2: contato.fonecelular2 || '',
            emailcontato: contato.emailcontato || '',
            sitecontato: contato.sitecontato || '',
            twittercontato: contato.twittercontato || '',
            facebookcontato: contato.facebookcontato || '',
            logradouro: contato.logradouro || '',
            numero: contato.numero || '',
            bairro: contato.bairro || '',
            complemento: contato.complemento || '',
            cep: contato.cep || '',
            codcidade: contato.codcidade || '',
            obsgeral: contato.obsgeral || '',
            cargo: contato.cargo || '',
            empresa: contato.empresa || '',
            inativo: contato.inativo || ANAO,
            codusuarioregistrou,
            removefotocontato: fotoRemove ? ASIM : ANAO,
            removeimagemcartao: cartaoRemove ? ASIM : ANAO,
            codigosgrupocontato: JSON.stringify(Array.from(gruposSelecionados))
        };

        const formData = appendFieldsToFormData(fields);

        if (fotoFile) {
            formData.append('fotocontato', fotoFile);
        }

        if (cartaoFile) {
            formData.append('imagemcartao', cartaoFile);
        }

        return formData;
    };

    /* Clicar para salvar */
    onClickSave = async () => {
        const { contato, isNewRecord } = this.state;
        const { history, loadContato } = this.props;

        const errors = this.validationRequiredFields();
        this.setState({ errors });

        if (Object.keys(errors).length > 0) {
            this.handleOpenAlert('Verifique os campos obrigatórios!');

            return;
        }

        try {
            const formData = this.getPayloadContato();

            if (isNewRecord) {
                await api.post('/contato', formData, MULTIPART_CONFIG);
            } else {
                await api.put(`/contato/${contato.codcontato}`, formData, MULTIPART_CONFIG);
            }

            if (loadContato) loadContato();

            history.push('/crm/contato');
        } catch (error) {
            console.error('Erro ao salvar:', error.response?.data || error.message);

            this.handleOpenAlert(
                getApiErrorMessage(error, 'Erro ao salvar!')
            );
        }
    };

    /* Clicar para cancelar */
    onClickCancel = () => {
        this.props.history.push('/crm/contato');
    };

    /* Valida os campos obrigatórios ao salvar */
    validationRequiredFields() {
        const { contato, schema } = this.state;

        return validateSchemaFields(contato, schema, ['nomecontato']);
    };

    /* Monta o bloco de upload de imagem */
    renderImageUploader = (prefix, label, altText) => {
        const { isView } = this.state;

        const preview = this.state[`${prefix}Preview`];
        const nome = this.state[`${prefix}Nome`];
        const isDragOver = this.state[`${prefix}DragOver`];
        const isModalOpen = this.state[`${prefix}ModalOpen`];
        const isRemoveModalOpen = this.state[`${prefix}RemoveModalOpen`];

        const hasImage = !!preview;
        const insertButtonLabel = hasImage ? 'Inserir Outro Arquivo' : 'Inserir Arquivo';
        const borderColor = isView ? theme.palette.grey[400] : isDragOver
            ? theme.palette.info.dark : hasImage ? theme.palette.success.main : theme.palette.grey[400];
        const backgroundColor = isView ? theme.palette.grey[100] : isDragOver
            ? theme.palette.info.light : hasImage
                ? alpha(theme.palette.success.main, 0.08) : theme.palette.grey[100];
        const infoTextColor = (hasImage && !isView) ? theme.palette.success.dark : theme.palette.grey[700];

        return (
            <Box>
                <Typography
                    color="textSecondary"
                    component="p"
                    variant="body2"
                >
                    {label}
                </Typography>

                <Box
                    onDragEnter={this.handleDragEnter(prefix)}
                    onDragOver={this.handleDragOver(prefix)}
                    onDragLeave={this.handleDragLeave(prefix)}
                    onDrop={this.handleDrop(prefix)}
                    style={{
                        width: '100%',
                        minHeight: 220,
                        border: `2px dashed ${borderColor}`,
                        borderRadius: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: backgroundColor,
                        marginTop: 8,
                        padding: '24px 20px',
                        boxSizing: 'border-box',
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    <CloudUploadOutlined
                        style={{
                            fontSize: 42,
                            color: hasImage ? theme.palette.success.main : theme.palette.grey[500],
                            marginBottom: 12
                        }}
                    />

                    <Typography
                        variant="body1"
                        style={{
                            color: theme.palette.grey[800],
                            textAlign: 'center',
                            marginBottom: 6
                        }}
                    >
                        {hasImage
                            ? 'Arquivo selecionado'
                            : 'Arraste um arquivo aqui ou selecione um no botão abaixo'
                        }
                    </Typography>

                    {nome ? (
                        <Typography
                            variant="caption"
                            style={{
                                display: 'block',
                                marginBottom: 16,
                                color: infoTextColor,
                                textAlign: 'center',
                                wordBreak: 'break-word'
                            }}
                        >
                            Arquivo: {nome}
                        </Typography>
                    ) : null}

                    <Button
                        component="label"
                        variant="contained"
                        color={!hasImage ? 'primary' : undefined}
                        disabled={isView}
                        style={{
                            textTransform: 'none',
                            minWidth: 190,
                            ...(hasImage && !isView && {
                                backgroundColor: theme.palette.success.main,
                                color: theme.palette.common.white
                            })
                        }}
                    >
                        {insertButtonLabel}

                        <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id={`upload-${prefix}-contato`}
                            type="file"
                            onChange={this.handleImageChange(prefix)}
                        />
                    </Button>
                </Box>

                <br />

                <Box
                    display="flex"
                    justifyContent="flex-end"
                    style={{ gap: 10 }}
                >
                    <Button
                        variant="contained"
                        onClick={this.handleOpenRemoveImageModal(prefix)}
                        disabled={!preview || isView}
                        style={{ textTransform: 'none' }}
                    >
                        Remover
                    </Button>

                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={this.handleOpenImageModal(prefix)}
                        disabled={!preview}
                        style={{ textTransform: 'none' }}
                    >
                        Visualizar
                    </Button>
                </Box>

                <DeleteConfirmationModal
                    open={isRemoveModalOpen}
                    handleClose={this.handleCloseRemoveImageModal(prefix)}
                    itemId=""
                    message={`Confirma remoção do arquivo de ${label.toLowerCase()} inserido?`}
                    handleDelete={this.confirmRemoveImage(prefix)}
                />

                <ImagePreviewModal
                    open={isModalOpen}
                    src={preview}
                    alt={altText}
                    onClose={this.handleCloseImageModal(prefix)}
                />
            </Box>
        );
    };

    render() {
        const {
            cidades,
            contato,
            errors,
            gruposContato,
            gruposSelecionados,
            isLoading,
            isView,
            messageAlert,
            openAlert
        } = this.state;

        if (isLoading) {
            return (
                <CardContent>
                    <Typography variant="body1">
                        Carregando contato...
                    </Typography>
                </CardContent>
            );
        }

        return (
            <div>
                <CardContent>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{ flexWrap: 'wrap' }}
                    >
                        <Typography
                            component="h1"
                            variant="h3"
                        >
                            Contato {contato.codcontato || ''}
                        </Typography>

                        <FormStatusSwitch
                            checked={contato.inativo !== ASIM}
                            name="inativo"
                            onChange={this.onChange}
                            disabled={isView}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        alignItems="center"
                        style={{
                            gap: 12,
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end'
                        }}
                    >
                        <fieldset
                            style={{
                                padding: '6px 12px',
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 12,
                                flex: '0 1 auto'
                            }}
                        >
                            <legend
                                style={{
                                    fontSize: 12,
                                    fontFamily: 'Calibri, sans-serif',
                                    padding: '0 4px',
                                    color: theme.palette.grey[700]
                                }}
                            >
                                Pessoa
                            </legend>

                            <RadioGroup
                                row
                                name="tipopessoa"
                                value={contato.tipopessoa}
                                onChange={this.onChange}
                            >
                                <FormControlLabel
                                    value={APESSOA_FISICA}
                                    label={<span>Física</span>}
                                    control={
                                        <Radio
                                            size="small"
                                            color="primary"
                                            disabled={isView}
                                        />
                                    }
                                />

                                <FormControlLabel
                                    value={APESSOA_JURIDICA}
                                    label={<span>Jurídica</span>}
                                    control={
                                        <Radio
                                            size="small"
                                            color="primary"
                                            disabled={isView}
                                        />
                                    }
                                />
                            </RadioGroup>
                        </fieldset>

                        <TextField
                            label="Data de nascimento"
                            name="datanasc"
                            onChange={this.onChange}
                            type="date"
                            value={getDateInputValue(contato.datanasc)}
                            variant="outlined"
                            disabled={isView}
                            InputLabelProps={{ shrink: true }}
                            style={{
                                minWidth: 180,
                                flex: '0 1 180px'
                            }}
                        />
                    </Box>

                    <br />

                    <Box>
                        <TextField
                            label="Nome"
                            name="nomecontato"
                            value={contato.nomecontato || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            helperText={errors.nomecontato || ''}
                            error={!!errors.nomecontato}
                            disabled={isView}
                            inputProps={{ maxLength: 100 }}
                            fullWidth
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="Cargo"
                            name="cargo"
                            value={contato.cargo || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 220
                            }}
                        />

                        <TextField
                            label="Empresa"
                            name="empresa"
                            value={contato.empresa || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 220
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="Telefone 1"
                            name="fonecontato1"
                            value={contato.fonecontato1 || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 160
                            }}
                        />

                        <TextField
                            label="Telefone 2"
                            name="fonecontato2"
                            value={contato.fonecontato2 || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 160
                            }}
                        />

                        <TextField
                            label="Celular 1"
                            name="fonecelular1"
                            value={contato.fonecelular1 || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 160
                            }}
                        />

                        <TextField
                            label="Celular 2"
                            name="fonecelular2"
                            value={contato.fonecelular2 || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 160
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="E-mail"
                            name="emailcontato"
                            value={contato.emailcontato || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 220
                            }}
                        />

                        <TextField
                            label="Site"
                            name="sitecontato"
                            value={contato.sitecontato || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 220
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="X (antigo Twitter)"
                            name="twittercontato"
                            value={contato.twittercontato || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 220
                            }}
                        />

                        <TextField
                            label="Facebook"
                            name="facebookcontato"
                            value={contato.facebookcontato || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 220
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="Logradouro"
                            name="logradouro"
                            value={contato.logradouro || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 2,
                                minWidth: 240
                            }}
                        />

                        <TextField
                            label="Número"
                            name="numero"
                            value={contato.numero || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 120
                            }}
                        />

                        <TextField
                            label="Bairro"
                            name="bairro"
                            value={contato.bairro || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 180
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="Complemento"
                            name="complemento"
                            value={contato.complemento || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 200
                            }}
                        />

                        <TextField
                            label="CEP"
                            name="cep"
                            value={contato.cep || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            style={{
                                flex: '0 1 140px',
                                minWidth: 140
                            }}
                        />

                        <FormControl
                            variant="outlined"
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 220
                            }}
                        >
                            <ComboPadrao
                                name={'codcidade'}
                                label={'Cidade'}
                                value={contato.codcidade}
                                onChange={this.onChange}
                                fieldId={'codcidade'}
                                listItems={cidades}
                                fieldDescription={'desccidadeuf'}
                            />
                        </FormControl>
                    </Box>

                    <br />

                    <FormTextArea
                        label="Observação"
                        name="obsgeral"
                        value={contato.obsgeral}
                        onChange={this.onChange}
                        disabled={isView}
                        minHeight={100}
                    />

                    <br />

                    <Box>
                        <Typography
                            color="textSecondary"
                            component="p"
                            variant="body2"
                        >
                            Grupos de contato
                        </Typography>

                        <Box
                            style={{
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 8,
                                padding: '8px 12px',
                                marginTop: 8,
                                maxHeight: 180,
                                overflowY: 'auto'
                            }}
                        >
                            {gruposContato.length === 0 && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    Nenhum grupo de contato cadastrado
                                </Typography>
                            )}

                            {gruposContato.map(grupo => (
                                <FormControlLabel
                                    key={grupo.codgrupocontato}
                                    label={grupo.descgrupocontato}
                                    style={{ display: 'flex' }}
                                    control={
                                        <Checkbox
                                            checked={gruposSelecionados.has(Number(grupo.codgrupocontato))}
                                            onChange={() => this.onChangeGrupoContato(Number(grupo.codgrupocontato))}
                                            color="primary"
                                            disabled={isView}
                                        />
                                    }
                                />
                            ))}
                        </Box>
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 20 }}
                    >
                        <Box
                            style={{
                                flex: 1,
                                minWidth: 280
                            }}
                        >
                            {this.renderImageUploader('foto', 'Foto', 'Foto do contato')}
                        </Box>

                        <Box
                            style={{
                                flex: 1,
                                minWidth: 280
                            }}
                        >
                            {this.renderImageUploader('cartao', 'Cartão de visita', 'Cartão de visita do contato')}
                        </Box>
                    </Box>

                    <br />
                    <br />

                    <FormActions
                        onSave={this.onClickSave}
                        onCancel={this.onClickCancel}
                        disableSave={isView}
                    />
                </CardContent>

                <AlertInformation
                    open={openAlert}
                    message={messageAlert}
                    handleClose={this.handleCloseAlert}
                />
            </div>
        );
    }
};

ContatoForm.propTypes = {
    history: PropTypes.object,
    match: PropTypes.object,
    location: PropTypes.object,
    loadContato: PropTypes.func
};

export default ContatoForm;